import { BlogPostMetadata } from '../types/blog';

// Import all MDX files
const modules = import.meta.glob<any>('../content/insights/*.mdx', { eager: true });

/**
 * Get all blog posts with their metadata
 */
export function getAllPosts(): BlogPostMetadata[] {
  const posts: BlogPostMetadata[] = [];

  for (const path in modules) {
    const module = modules[path];
    
    // Check if we need to access frontmatter differently
    const frontmatter = module.frontmatter || module;
    
    // remark-mdx-frontmatter exports frontmatter properties directly
    const metadata: BlogPostMetadata = {
      id: frontmatter.id,
      title: frontmatter.title,
      excerpt: frontmatter.excerpt,
      category: frontmatter.category,
      date: formatDate(frontmatter.date),
      readTime: frontmatter.readTime,
      author: frontmatter.author,
      authorRole: frontmatter.authorRole,
      imageUrl: frontmatter.imageUrl,
      featured: frontmatter.featured,
      tags: frontmatter.tags,
    };
    
    if (metadata.id && metadata.title) {
      posts.push(metadata);
    }
  }

  // Sort by date, newest first
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * Get a single blog post by ID
 */
export async function getPostById(id: string) {
  for (const path in modules) {
    const module = modules[path];
    const frontmatter = module.frontmatter || module;

    if (frontmatter.id === id) {
      const metadata: BlogPostMetadata = {
        id: frontmatter.id,
        title: frontmatter.title,
        excerpt: frontmatter.excerpt,
        category: frontmatter.category,
        date: formatDate(frontmatter.date),
        readTime: frontmatter.readTime,
        author: frontmatter.author,
        authorRole: frontmatter.authorRole,
        imageUrl: frontmatter.imageUrl,
        featured: frontmatter.featured,
        tags: frontmatter.tags,
      };

      return {
        metadata,
        Component: module.default,
      };
    }
  }
  return null;
}

/**
 * Get featured posts
 */
export function getFeaturedPosts(): BlogPostMetadata[] {
  return getAllPosts().filter(post => post.featured);
}

/**
 * Get related posts based on category and tags
 */
export function getRelatedPosts(currentPostId: string, limit: number = 3): BlogPostMetadata[] {
  const allPosts = getAllPosts();
  const currentPost = allPosts.find(p => p.id === currentPostId);
  
  if (!currentPost) {
    return allPosts.slice(0, limit);
  }

  // Score posts by relevance
  const scoredPosts = allPosts
    .filter(p => p.id !== currentPostId)
    .map(post => {
      let score = 0;
      
      // Same category
      if (post.category === currentPost.category) {
        score += 10;
      }
      
      // Shared tags
      if (post.tags && currentPost.tags) {
        const sharedTags = post.tags.filter(tag => currentPost.tags?.includes(tag));
        score += sharedTags.length * 5;
      }
      
      return { post, score };
    })
    .sort((a, b) => b.score - a.score);

  return scoredPosts.slice(0, limit).map(item => item.post);
}

/**
 * Format date string to readable format
 */
function formatDate(dateString: string | undefined): string {
  if (!dateString) {
    return 'Date not available';
  }
  
  // If already in readable format, return as is
  if (dateString.includes(',')) {
    return dateString;
  }
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Calculate reading progress percentage
 */
export function calculateReadingProgress(element: HTMLElement): number {
  const windowHeight = window.innerHeight;
  const documentHeight = element.scrollHeight;
  const scrollTop = window.scrollY;
  
  const trackLength = documentHeight - windowHeight;
  const progress = (scrollTop / trackLength) * 100;
  
  return Math.min(Math.max(progress, 0), 100);
}
